<template>
  <div
    v-hotkey="shortcuts"
    v-click-outside="unselect"
    class="event"
    :class="[eventColorStyle, { isSelected, isSingleEvent }]"
    @click.prevent="$emit('select', event.id)"
    @dblclick="openEvent($event)"
    @contextmenu.prevent.stop="showContextMenu"
  >
    <div class="calendar-border" />
    <span class="title">{{ event.title }}</span>
  </div>
</template>

<script lang="ts">
import {
  computed,
  defineComponent,
  onBeforeUnmount,
  PropType,
  toRefs,
} from "vue";

import { FullEvent } from "@ctrl/controller-rpc";

import { useEventInfo } from "@/components/eventInfo";
import * as inject from "@/components/inject";
import { getEventShortcuts } from "@/components/object-shortcuts";
import {
  assignShortcuts,
  Shortcuts,
  unSelectItem,
} from "@/components/shortcuts";
import { eventColor, isCancellable } from "@/event";
import { bindAsync, releasableRef, useReleaser } from "@/ref";
import { navigateToEvent } from "@/utils/navigate";

export default defineComponent({
  name: "EventAllDay",
  props: {
    event: {
      type: Object as PropType<FullEvent>,
      required: true,
    },
    isSelected: {
      type: Boolean,
      default: false,
    },
    isSingleEvent: {
      type: Boolean,
      default: false,
    },
  },
  emits: {
    select: (_id: string) => true,
    unselect: () => true,
  },
  async setup(props, { emit }) {
    const { event } = toRefs(props);
    const theme = inject.theme();
    const controller = inject.controller();
    const logger = inject.logger();
    const profile = inject.profile();
    const calendars = inject.calendars();
    const router = inject.router();
    const contextMenu = inject.contextMenu();
    const isInputFocused = inject.isInputFocused();
    const isModalShown = inject.isModalShown();
    const simpleModal = inject.simpleModal();
    const multipleEventsModal = inject.multipleEventsModal();
    const menu = inject.menu();
    const palette = inject.palette();
    const releaser = useReleaser();

    const eventInfo = useEventInfo(controller, calendars, event);
    onBeforeUnmount(() =>
      eventInfo.then((eventInfo) => eventInfo.releaseEventInfo())
    );
    const taskPromise = releaser.add(
      bindAsync(event, async ({ task }) =>
        task ? await controller.task.get(task) : releasableRef(null)
      )
    );
    const [{ calendar, isWritable }, task] = await Promise.all([
      eventInfo,
      taskPromise,
    ]);

    const openEvent = (mouseEvent?: MouseEvent) => {
      navigateToEvent(router, controller, event.value, mouseEvent);
    };

    const showContextMenu = async (e: MouseEvent) => {
      emit("select", event.value.id);
      contextMenu.value.show(
        {
          type: "event",
          value: {
            ...event.value,
            isCancellable: await isCancellable(event, controller),
            isDeletable: !!calendar.value?.isWritable,
          },
        },
        e
      );
    };

    const eventColorStyle = computed(() => eventColor(calendar, theme));

    const isEscapeActive = computed<boolean>(
      () => props.isSelected && !contextMenu.value.isDisplayed
    );

    const shortcuts = computed<Shortcuts>(() => {
      const injection = { isModalShown, isInputFocused };
      return props.isSelected
        ? {
            ...assignShortcuts(
              injection,
              unSelectItem,
              () => emit("unselect"),
              isEscapeActive
            ),
            ...getEventShortcuts(
              { event, task, isWritable, calendar },
              {
                ...injection,
                controller,
                logger,
                profile,
                router,
                simpleModal,
                multipleEventsModal,
                menu,
                palette,
              },
              { closeContextMenu: contextMenu.value.hide }
            ),
          }
        : {};
    });

    const unselect = () => {
      if (props.isSelected) {
        emit("unselect");
      }
    };

    return {
      eventColorStyle,
      openEvent,
      showContextMenu,
      shortcuts,
      unselect,
    };
  },
});
</script>

<style scoped lang="scss">
.event {
  padding-block: 6px;
  padding-inline-end: 10px;
  padding-inline-start: 16px;
  height: 20px;
  width: calc(50% - 37px);
  border-radius: $border-radius-s;
  transition: opacity 0.2s ease;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  position: relative;

  &.isSingleEvent {
    width: calc(100% - 40px);
  }

  &.isSelected {
    box-shadow: 0px 1px 1px rgba(0, 0, 0, 0.05);
    @include themed() {
      border: 1px solid t($grey-50);
    }
  }
  &:not(.isSelected):hover {
    @include themed() {
      border-color: t($grey-30);
    }
  }

  .calendar-border {
    position: absolute;
    inset-inline-start: 0px;
    top: 50%;
    transform: translateY(-50%);
    width: 3px;
    height: 20px;
    border-radius: 10px;
  }

  .title {
    @extend %font-caption-regular;
  }

  &.calendar-a--light {
    background: $calendar-a-20;
    border: 1px solid $calendar-a-20;
    color: $calendar-a-80;
  }

  &.calendar-a--dark {
    background: $calendar-a-90;
    border: 1px solid $calendar-a-90;
    color: $calendar-a-20;
  }

  &.calendar-b--light {
    background: $calendar-b-20;
    border: 1px solid $calendar-b-20;
    color: $calendar-b-80;
  }

  &.calendar-b--dark {
    background: $calendar-b-90;
    border: 1px solid $calendar-b-90;
    color: $calendar-b-20;
  }

  &.calendar-c--light {
    background: $calendar-c-20;
    border: 1px solid $calendar-c-20;
    color: $calendar-c-80;
  }

  &.calendar-c--dark {
    background: $calendar-c-90;
    border: 1px solid $calendar-c-90;
    color: $calendar-c-20;
  }

  &.calendar-d--light {
    background: $calendar-d-20;
    border: 1px solid $calendar-d-20;
    color: $calendar-d-80;
  }

  &.calendar-d--dark {
    background: $calendar-d-90;
    border: 1px solid $calendar-d-90;
    color: $calendar-d-20;
  }

  &.calendar-e--light {
    background: $calendar-e-20;
    border: 1px solid $calendar-e-20;
    color: $calendar-e-80;
  }

  &.calendar-e--dark {
    background: $calendar-e-90;
    border: 1px solid $calendar-e-90;
    color: $calendar-e-20;
  }

  &.calendar-f--light {
    background: $calendar-f-20;
    border: 1px solid $calendar-f-20;
    color: $calendar-f-80;
  }

  &.calendar-f--dark {
    background: $calendar-f-90;
    border: 1px solid $calendar-f-90;
    color: $calendar-f-20;
  }

  &.calendar-g--light {
    background: $calendar-g-20;
    border: 1px solid $calendar-g-20;
    color: $calendar-g-80;
  }

  &.calendar-g--dark {
    background: $calendar-g-90;
    border: 1px solid $calendar-g-90;
    color: $calendar-g-20;
  }

  &.calendar-h--light {
    background: $calendar-h-20;
    border: 1px solid $calendar-h-20;
    color: $calendar-h-80;
  }

  &.calendar-h--dark {
    background: $calendar-h-90;
    border: 1px solid $calendar-h-90;
    color: $calendar-h-20;
  }

  &.calendar-i--light {
    background: $calendar-i-20;
    border: 1px solid $calendar-i-20;
    color: $calendar-i-80;
  }

  &.calendar-i--dark {
    background: $calendar-i-90;
    border: 1px solid $calendar-i-90;
    color: $calendar-i-20;
  }

  &.calendar-a--light,
  &.calendar-a--dark {
    .calendar-border {
      background-color: $calendar-a-50;
    }
  }

  &.calendar-b--light,
  &.calendar-b--dark {
    .calendar-border {
      background-color: $calendar-b-50;
    }
  }

  &.calendar-c--light,
  &.calendar-c--dark {
    .calendar-border {
      background-color: $calendar-c-50;
    }
  }

  &.calendar-d--light,
  &.calendar-d--dark {
    .calendar-border {
      background-color: $calendar-d-50;
    }
  }

  &.calendar-e--light,
  &.calendar-e--dark {
    .calendar-border {
      background-color: $calendar-e-50;
    }
  }

  &.calendar-f--light,
  &.calendar-f--dark {
    .calendar-border {
      background-color: $calendar-f-50;
    }
  }

  &.calendar-g--light,
  &.calendar-g--dark {
    .calendar-border {
      background-color: $calendar-g-50;
    }
  }

  &.calendar-h--light,
  &.calendar-h--dark {
    .calendar-border {
      background-color: $calendar-h-50;
    }
  }

  &.calendar-i--light,
  &.calendar-i--dark {
    .calendar-border {
      background-color: $calendar-i-50;
    }
  }
}
</style>
